import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../../common/Modal'
import { CustomAlertConditions, postCustomAlertRequest } from '../../../../states/types/setting'
import { IconName } from '@fortawesome/fontawesome-common-types'
import Tips from '../../common/Tips'
import { replaceAndStrip } from '../../../../utils/string'
import { compile } from '../../../../utils/search'
import { sortBy } from 'lodash'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { nowDate } from '../../../../utils/date'
import LoadingSquare from '../../common/LoadingSquare'

interface Props {
  closeCallback: VoidFunction
  addCallback: (params: postCustomAlertRequest, callback: VoidFunction) => void
  isDisplay: boolean
}

interface Errors {
  alert_title: string,
  condition: string,
  team: string,
  category_time: string,
  category_biz_point: string,
  application_time: string,
  application_biz_point: string,
  slack: string,
}

interface DisplayTeamList {
  id: number
  name: string
  checked: boolean
  display: boolean
}

interface Condition {
  condition_type: string
  operator_type: string
  detail: string | null
  target: string | null
}

interface TemplateCondition {
  title: string[]
  value: string
  description: string
  bg_color: string
  icon: string
  conditions: Condition[],
}

interface DisplayApplicationList {
  name: string
  checked: boolean
  display: boolean
}

const SELECT_TYPE = [
  {
    title: ["働き方のアラート"],
    value: "work",
    description: "働き方に関するアラートを作成したい",
    bg_color: "#FF0000",
    icon: "building",
    conditions: [],
  },
  {
    title: ["アプリのアラート"],
    value: "application",
    description: "アプリの使用状況に関するアラートを作成したい",
    bg_color: "#008800",
    icon: "icons",
    conditions: [],
  },
  {
    title: ["仕分け率のアラート"],
    value: "rate",
    description: "業務仕分けに関するアラートを作成したい",
    bg_color: "#0000FF",
    icon: "tags",
    conditions: [],
  },
]

const SELECT_WORK = [
  {
    title: ["長時間勤務アラート"],
    value: "work_long_time",
    description: "長時間勤務しているメンバーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "stopwatch",
    conditions: [
      {
        condition_type: "daily_work_time",
        operator_type: "more_than_or_equal",
        detail: "12",
        target: null,
      },
    ],
  },
  {
    title: ["残業アラート"],
    value: "work_over_time",
    description: "残業しているメンバーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "moon",
    conditions: [
      {
        condition_type: "daily_over_time",
        operator_type: "more_than_or_equal",
        detail: "2",
        target: null,
      },
    ],
  },
  {
    title: ["早朝勤務アラート"],
    value: "work_early_time",
    description: "早朝勤務しているメンバーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "cloud-sun",
    conditions: [
      {
        condition_type: "start_time",
        operator_type: "less_than_or_equal",
        detail: "07:00",
        target: null,
      },
    ],
  },
  {
    title: ["深夜勤務アラート"],
    value: "work_later_time",
    description: "深夜勤務しているメンバーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "cloud-moon",
    conditions: [
      {
        condition_type: "end_time",
        operator_type: "more_than_or_equal",
        detail: "22:00",
        target: null,
      },
    ],
  },
  {
    title: ["休日勤務アラート"],
    value: "work_holiday",
    description: "休日勤務しているメンバーを発見して、アラートします",
    bg_color: "#e35e0b",
    icon: "mug-hot",
    conditions: [
      {
        condition_type: "holiday_work_time",
        operator_type: "more_than_or_equal",
        detail: "1",
        target: null,
      },
    ],
  },
]

const SELECT_APPLICATION = [
  {
    title: ["アプリ使用時間アラート"],
    value: "application_time",
    description: "アプリの使用時間を集計して、アラートします",
    bg_color: "#09b837",
    icon: "clock",
    conditions: [
      {
        condition_type: "application_time",
        operator_type: "more_than_or_equal",
        detail: "1",
        target: null,
      },
    ],
  },
  {
    title: ["アプリ操作量アラート"],
    value: "application_biz",
    description: "アプリの操作量を集計して、アラートします",
    bg_color: "#09b837",
    icon: "keyboard",
    conditions: [
      {
        condition_type: "application_biz_point",
        operator_type: "more_than_or_equal",
        detail: "1000",
        target: null,
      },
    ],
  },
]

const SELECT_RATE = [
  {
    title: ["仕分け率が低い", "ユーザーのアラート"],
    value: "rate_low",
    description: "業務仕分け率が低いメンバーを発見して、アラートします",
    bg_color: "#2080fd",
    icon: "star-half",
    conditions: [
      {
        condition_type: "labeling_rate",
        operator_type: "less_than_or_equal",
        detail: "60",
        target: null,
      },
    ],
  },
  {
    title: ["仕分け率が100%でない", "ユーザーのアラート"],
    value: "rate_under_100",
    description: "業務仕分け率が100%でないメンバーを発見して、アラートします",
    bg_color: "#2080fd",
    icon: "star",
    conditions: [
      {
        condition_type: "labeling_rate",
        operator_type: "less_than_or_equal",
        detail: "99",
        target: null,
      },
    ],
  },
]

const CreateCustomAlertWizardModal: FC<Props> = (props) => {
  const {
    closeCallback,
    addCallback,
    isDisplay,
  } = props

  const {
    commonPart,
    teamList,
    system,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [step, setStep] = useState<string>("intro")

  const initErrors = {
    alert_title: "",
    condition: "",
    team: "",
    category_time: "",
    category_biz_point: "",
    application_time: "",
    application_biz_point: "",
    slack: "",
  }

  const [alertTitle, setAlertTitle] = useState<string>("")
  const [team, setTeam] = useState<DisplayTeamList[]>([])
  const [isDisplayTeamSelectBox, setIsDisplayTeamSelectBox] = useState<boolean>(false)
  const [searchTeamWord, setSearchTeamWord] = useState<string>("")

  const [selectCondition, setSelectCondition] = useState<TemplateCondition | null>(null)

  const [applicationList, setApplicationList] = useState<DisplayApplicationList[]>([])
  const [isDisplayApplicationSelectBox, setIsDisplayApplicationSelectBox] = useState<boolean>(false)
  const [searchApplicationWord, setSearchApplicationWord] = useState<string>("")

  const [errors, setErrors] = useState<Errors>(initErrors)

  const selectList = useMemo(() => {
    if (step === "select") {
      return SELECT_TYPE
    }
    if (step === "work") {
      return SELECT_WORK
    }
    if (step === "application") {
      return SELECT_APPLICATION
    }
    if (step === "rate") {
      return SELECT_RATE
    }
    return []
  }, [step])

  const close = () => {
    setStep("intro")
    setApplicationList(applicationList?.map((value) => ({ ...value, checked: false, display: true })))
    setTeam(team?.map((value) => ({ ...value, checked: false, display: true })))
    closeCallback()
  }

  const selectTarget = (condition: TemplateCondition) => {
    setSelectCondition(condition)
    if (["application_time", "application_biz"].includes(condition.value)) {
      setStep("select_app")
    } else {
      setStep("target")
    }
  }

  const replaceAndStripTitle = (event: React.FocusEvent<HTMLInputElement>) => {
    setAlertTitle(replaceAndStrip(event.target.value))
    if (event.target.value) {
      setErrors({
        ...errors,
        alert_title: "",
      })
    }
  }

  const clickTeam = (teamId: number) => {
    setIsDisplayTeamSelectBox(false)
    const list = team?.map((value) => (
      value.id === teamId
        ? { ...value, checked: true, display: true }
        : { ...value, checked: false, display: true }
    ))
    setTeam(list)
    setSearchTeamWord("")
  }

  const searchTeam = (words: string) => {
    setSearchTeamWord(words)
    if (!words) {
      setTeam(team?.map((value) => ({ ...value, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = team?.map((value) => {
      if (compile(word).test(value.name)) {
        return { ...value, display: true }
      }
      return { ...value, display: false }
    })
    setTeam(sortBy(list, 'id'))
  }

  const resetSearchTeam = () => {
    setSearchTeamWord("")
    setTeam(team?.map((value) => ({ ...value, display: true })))
  }

  const openTeamSelectBox = () => {
    if (!team.length) return
    setIsDisplayTeamSelectBox(true)
    setErrors({
      ...errors,
      team: "",
    })
  }

  const clickApplication = (word: string) => {
    setIsDisplayApplicationSelectBox(false)
    const list = applicationList?.map((value) => (
      value.name === word
        ? { ...value, checked: true, display: true }
        : { ...value, checked: false, display: true }
    ))
    setApplicationList(list)
    setSearchApplicationWord("")
    setErrors({
      ...errors,
      application_biz_point: "",
    })
  }

  const searchApplication = (words: string) => {
    setSearchApplicationWord(words)
    if (!words) {
      setApplicationList(applicationList?.map((value) => ({ ...value, display: true })))
      return
    }
    const word = replaceAndStrip(words).split(/\s+/)
    const list = applicationList?.map((value) => {
      if (compile(word).test(value.name)) {
        return { ...value, display: true }
      }
      return { ...value, display: false }
    })
    setApplicationList(sortBy(list, 'id'))
  }

  const resetSearchApplication = () => {
    setSearchApplicationWord("")
    setApplicationList(applicationList?.map((value) => ({ ...value, display: true })))
  }

  const buildSelect = () => {
    const tmp = []
    if (["select_app", "target", "name", "confirm"].includes(step)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート：
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                borderRadius: "4px",
                backgroundColor: selectCondition?.bg_color,
                color: "#FFFFFF",
                padding: "2px 4px",
              }}
            >
              <FontAwesomeIcon icon={['fas', selectCondition?.icon as IconName]} />
              {selectCondition?.title.join("")}
            </div>
          </div>
        </>,
      )
    }
    if (["target", "name", "confirm"].includes(step) && !!applicationList.find((value) => value.checked)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート対象アプリ：
            <div>
              {applicationList.find((value) => value.checked)?.name || "-"}
            </div>
          </div>
        </>,
      )
    }
    if (["name", "confirm"].includes(step)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート対象チーム：
            <div>
              {team.find((value) => value.checked)?.name || "-"}
            </div>
          </div>
        </>,
      )
    }
    if (["confirm"].includes(step)) {
      tmp.push(
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "0 8px",
            }}
          >
            アラート名：
            <div>
              {alertTitle}
            </div>
          </div>
        </>,
      )
    }
    return (
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "8px",
          marginBottom: "8px",
          borderRadius: "4px",
          padding: "8px",
          backgroundColor: ["confirm"].includes(step) ? "#FFFFFF" : "#EEEEEE",
          border: ["confirm"].includes(step) ? "solid 1px #CCCCCC" : "none",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
          }}
        >
          {["confirm"].includes(step) ? "作成されるアラート" : "現在の選択"}
        </div>
        {tmp.map((v) => v)}
      </div>
    )
  }

  const buildTmpName = () => {
    let tmpName = ""
    const prefix = nowDate("YYYYMMDD")
    if (applicationList.find((v) => v.checked)) {
      tmpName = `${prefix}_${selectCondition?.title.join("")}_${applicationList.find((v) => v.checked)?.name}_${team.find((v) => v.checked)?.name}`
    } else {
      tmpName = `${prefix}_${selectCondition?.title.join("")}_${team.find((v) => v.checked)?.name}`
    }
    setAlertTitle(tmpName)
  }

  const submit = () => {
    setStep("submit")
    const tmpConditions: CustomAlertConditions[] = []
    selectCondition?.conditions.forEach((v) => {
      if (["daily_work_time", "daily_over_time", "holiday_work_time"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          biz_point: null,
          set_hour: null,
          set_minute: null,
          alert_target: v.target,
          hours: Number(v.detail),
          value: null,
        })
      } else if (["start_time", "end_time"].includes(v.condition_type)) {
        const tmpTime = v.detail?.split(":") || []
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          biz_point: null,
          set_hour: tmpTime[0] || "10",
          set_minute: tmpTime[1] || "00",
          alert_target: v.target,
          hours: null,
          value: null,
        })
      } else if (["application_time"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          biz_point: null,
          set_hour: v.detail,
          set_minute: "00",
          alert_target: applicationList.find((w) => w.checked)?.name || "",
          hours: null,
          value: null,
        })
      } else if (["application_biz_point"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          biz_point: v.detail,
          set_hour: null,
          set_minute: null,
          alert_target: applicationList.find((w) => w.checked)?.name || "",
          hours: null,
          value: null,
        })
      } else if (["labeling_rate"].includes(v.condition_type)) {
        tmpConditions.push({
          condition_type: v.condition_type,
          operator_type: v.operator_type,
          biz_point: null,
          set_hour: null,
          set_minute: null,
          alert_target: v.target,
          hours: null,
          value: v.detail,
        })
      }
    })
    addCallback(
      {
        member_id: commonPart.current_member?.id,
        name: alertTitle,
        send_timing_type: "next_day",
        send_timing_hour: "10",
        send_timing_minute: "00",
        send_to_member: false,
        team_ids: [team.find((v) => v.checked)?.id || 0],
        team_member_ids: [],
        custom_alert_conditions: tmpConditions,
        notice_type: "mail",
        slack_channel_id: null,
        slack_channel_name: null,
        slack_channel_private: false,
      },
      () => {
        setStep("completed")
      },
    )
  }

  useEffect(() => {
    const tmpTeam = (commonPart.role?.includes('administrator'))
      ? teamList.teams
      : teamList.teams?.filter((value) => commonPart.team_manager_ids?.includes(value.team_id))
    setTeam(
      tmpTeam.map((value) => ({ id: value.team_id, name: value.name, checked: false, display: true })),
    )
  }, [isDisplay])

  useEffect(() => {
    if (!system.company_service) return
    setApplicationList(
      system.company_service.application_names.map((value) => ({ name: value, checked: false, display: true })),
    )
  }, [system.company_service])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="create_custom_alert_wizard_modal custom_alert"
      closeCallback={close}
      closeable
    >
      <>
        {
          step === "intro" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  カスタムアラート簡単設定でカスタムアラートを作成しましょう！
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src="/static/images/custom_alert/image_1.png" alt="" />
                  </div>
                  <div
                    style={{
                      padding: "4px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                      color: "#333333",
                    }}
                  >
                    <div>
                      カスタムアラート簡単設定ではいくつかの質問に回答すると、カスタムアラートを作成できます！
                    </div>
                    <div>
                      作成したカスタムアラートは後からいつでも変更することができます。
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "24px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  className="button"
                  onClick={() => setStep("select")}
                >
                  アラートを作成しましょう
                </button>
              </div>
            </>
          )
        }
        {
          ["select", "work", "application", "rate"].includes(step) && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  どんなカスタムアラートを作成しますか？
                </div>
                {
                  step !== "select" && (
                    <>
                      <div>
                        <button
                          type="button"
                          onClick={() => setStep("select")}
                          style={{
                            fontSize: "11px",
                          }}
                        >
                          &lt; ひとつ前へ戻る
                        </button>
                      </div>
                    </>
                  )
                }
                <div className="select_button_area">
                  {
                    selectList.map((v) => (
                      <React.Fragment key={v.title.join()}>
                        <Tips
                          description={v.description}
                          className=""
                          icon={false}
                          style={{
                            width: "calc((100% - 48px) / 3)",
                          }}
                        >
                          <button
                            type="button"
                            style={{
                              backgroundColor: v.bg_color,
                            }}
                            onClick={() => (step === "select" ? setStep(v.value) : selectTarget(v))}
                          >
                            <div
                              style={{
                                fontSize: "40px",
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', v.icon as IconName]} />
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                display: "flex",
                                flexFlow: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "4px",
                              }}
                            >
                              {v.title.map((w) => (
                                <div>
                                  {w}
                                </div>
                              ))}
                            </div>
                          </button>
                        </Tips>
                      </React.Fragment>
                    ))
                  }
                </div>
              </div>
            </>
          )
        }
        {
          step === "select_app" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  アラートの対象アプリを選びましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      setApplicationList(applicationList?.map((value) => ({ ...value, checked: false })))
                      setStep("select")
                      setIsDisplayTeamSelectBox(false)
                    }}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div className="item">
                  <div className="title">
                    アラート対象アプリ
                  </div>
                  <div className="conditions_item select" style={{ width: "100%" }}>
                    <div className="selected_item">
                      <div
                        className="modal_trigger"
                        role="none"
                        onClick={() => setIsDisplayApplicationSelectBox(true)}
                      />
                      <p>
                        {
                          applicationList.find((value) => value.checked)?.name || "-"
                        }
                      </p>
                      <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon" />
                    </div>
                    <div
                      className={`select_box custom_alert_select ${isDisplayApplicationSelectBox || "none"}`}
                      style={{ top: "unset" }}
                    >
                      <div className="search_area">
                        <FontAwesomeIcon
                          icon={['fas', 'search']}
                          className="icon"
                        />
                        <input
                          type="text"
                          placeholder="アプリ名で検索"
                          onChange={(event) => searchApplication(event.target.value)}
                          value={searchApplicationWord}
                        />
                        {
                          searchApplicationWord && (
                            <button type="button" className="reset" onClick={resetSearchApplication}>
                              <FontAwesomeIcon icon={['far', 'times-circle']} />
                            </button>
                          )
                        }
                      </div>
                      <div className="select_list">
                        <div
                          key={0}
                          className="select_row single"
                          onClick={() => clickApplication("-")}
                          role="none"
                        >
                          -
                        </div>
                        {
                          applicationList && applicationList.map((value: DisplayApplicationList) => (
                            value.display && (
                              <div
                                key={value.name}
                                className="select_row single"
                                onClick={() => clickApplication(value.name)}
                                role="none"
                              >
                                {value.name}
                              </div>
                            )
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      setStep("target")
                      setIsDisplayApplicationSelectBox(false)
                    }}
                    disabled={!applicationList.find((value) => value.checked)}
                  >
                    次は対象チームを選びましょう
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "target" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  アラートの対象チームを選びましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("select")
                      setIsDisplayTeamSelectBox(false)
                      setApplicationList(applicationList?.map((value) => ({ ...value, checked: false })))
                    }}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div className="item">
                  <div className="title">
                    アラート対象チーム
                  </div>
                  <div className="conditions_item" style={{ width: "100%" }}>
                    <div className="selected_item">
                      <div
                        className="modal_trigger"
                        role="none"
                        onClick={openTeamSelectBox}
                      />
                      <p>
                        {
                          team.find((value) => value.checked)?.name || "-"
                        }
                      </p>
                      <FontAwesomeIcon icon={['fas', 'caret-down']} className="icon" />
                    </div>
                    <div
                      className={`select_box custom_alert_select ${isDisplayTeamSelectBox || "none"}`}
                      style={{ top: "unset" }}
                    >
                      <div className="search_area">
                        <FontAwesomeIcon
                          icon={['fas', 'search']}
                          className="icon"
                        />
                        <input
                          type="text"
                          placeholder="チーム名で検索"
                          onChange={(event) => searchTeam(event.target.value)}
                          value={searchTeamWord}
                        />
                        {
                          searchTeamWord && (
                            <button type="button" className="reset" onClick={resetSearchTeam}>
                              <FontAwesomeIcon icon={['far', 'times-circle']} />
                            </button>
                          )
                        }
                      </div>
                      <div className="select_list">
                        <div
                          key={0}
                          className="select_row single"
                          onClick={() => clickTeam(0)}
                          role="none"
                        >
                          -
                        </div>
                        {
                          team && team.map((value: DisplayTeamList) => (
                            value.display && (
                              <div
                                key={value.id}
                                className="select_row single"
                                onClick={() => clickTeam(value.id)}
                                role="none"
                              >
                                {value.name}
                              </div>
                            )
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={() => {
                      setStep("name")
                      buildTmpName()
                      setIsDisplayTeamSelectBox(false)
                    }}
                    disabled={!team.find((value) => value.checked)}
                  >
                    次は名前を決めましょう
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "name" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  アラートの名前を決めましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setStep("target")}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div className="item">
                  <div className="title">
                    アラート名
                  </div>
                  <div className="detail row_1">
                    <input
                      type="text"
                      placeholder="わかりやすい名称にすると、通知の際に便利です"
                      onChange={(event) => setAlertTitle(event.target.value)}
                      onBlur={(event) => replaceAndStripTitle(event)}
                      value={alertTitle}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={() => setStep("confirm")}
                    disabled={!alertTitle}
                  >
                    もう少しで作成完了です！
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "confirm" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  作成されるアラートを確認しましょう
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setStep("name")}
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    &lt; ひとつ前へ戻る
                  </button>
                </div>
                {buildSelect()}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <button
                    type="button"
                    className="button"
                    onClick={submit}
                    disabled={!alertTitle}
                  >
                    アラートを作成！
                  </button>
                </div>
              </div>
            </>
          )
        }
        {
          step === "submit" && (
            <>
              <LoadingSquare>
                <>
                  アラートを作成しています…
                </>
              </LoadingSquare>
            </>
          )
        }
        {
          step === "completed" && (
            <>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "4px",
                    fontSize: "18px",
                    marginBottom: "16px",
                  }}
                >
                  カスタムアラートの作成が完了しました！
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img src="/static/images/custom_alert/image_2.png" alt="" />
                  </div>
                  <div
                    style={{
                      padding: "4px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "4px",
                      color: "#333333",
                    }}
                  >
                    <div>
                      カスタムアラートの作成が完了しました！
                    </div>
                    <div>
                      細かい条件の変更・追加は「アラートの編集」からおこなうことができます！
                    </div>
                    <div>
                      様々な条件設定を駆使して、アラートをご活用ください！
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "24px",
                  marginTop: "24px",
                }}
              >
                <button
                  type="button"
                  className="button"
                  onClick={close}
                >
                  アラート作成完了！
                </button>
              </div>
            </>
          )
        }
      </>
    </Modal>
  )
}

export default CreateCustomAlertWizardModal
